import {sendSessionEmailInvite} from '@/api/api';
import {useGetMboVisits} from '@/api/Booking/useGetMboVisits';
import {useGetPrograms} from '@/api/Classes/useGetPrograms';
import {useGetPlayerAppointments} from '@/api/MLSChallenge/useGetPlayerAppointments';
import {useIsV3SessionViewEnabled} from '@/api/MLSChallenge/useIsV3SessionViewEnabled';
import {AthleteInfo, useGetAthleteInfo} from '@/api/Trainer/useGetAthleteInfo';
import DefaultTocaLogo from '@/assets/img/mls/toca-soccer-black-circle-logo.png';
import {Button as ConnectsButton} from '@/components/Connects/Button';
import {ButtonTray} from '@/components/Connects/ButtonTray';
import {LogoBanner} from '@/components/Connects/LogoBanner';
import {PageHeading} from '@/components/Connects/PageHeading';
import {SessionRecap} from '@/components/Connects/SessionRecap/SessionRecap';
import {LoadingAnimationFullHeight} from '@/components/Loader/LoadingAnimation';
import {Button} from '@/components/MLS/Button/Button';
import {ProfileBar} from '@/components/ProfileBar/ProfileBar';
import {useColors} from '@/hooks/MLSChallenge/usePatterns';
import {Session} from '@/pages/PlayerHome/MySessions/Session';
import {UpcomingSessions} from '@/pages/PlayerHome/MySessions/UpcomingSessions';
import {NavHeading} from '@/pages/PlayerHome/NavHeading/NavSection';
import {PlayerCardDisplay} from '@/pages/PlayerHome/PlayerCard/PlayerCardDisplay';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useEffect} from 'react';
import {Helmet} from 'react-helmet';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {toast} from 'react-toastify';

export const Connects = () => {
  const navigate = useNavigate();
  const {trainerID} = useParams(); // TODO are we actually using this for anything?
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('mboClientId');
  const siteId = searchParams.get('mboSiteId');
  useGetPrograms({siteId});
  useGetMboVisits({clientId, siteId});
  const {data: athleteInfo, isLoading} = useGetAthleteInfo({clientId, siteId});
  const queryClient = useQueryClient();

  const isV3SessionViewEnabled = useIsV3SessionViewEnabled();

  const {data: playerAppointments, isLoading: isLoadingPlayerAppointments} = useGetPlayerAppointments({
    playerProfileId: athleteInfo?.profileId || '',
    includeFutureAppointments: true,
    sessionSummariesLimit: 2,
    includeSessionSummary: true,
    futureAppointmentsLimit: 6,
    enabled: true,
  });

  const sessionSummaries = playerAppointments?.sessionSummaries?.summaries ?? [];
  const futureAppointments = playerAppointments?.futureAppointments?.appointments ?? [];
  const hasAuthAccount = athleteInfo?.hasAuthAccount ?? false;

  const sendSessionEmailInviteMutation = useMutation({
    mutationFn: () => sendSessionEmailInvite(clientId!, siteId!),
    onSuccess: () => {
      toast.success('Signup link sent successfully');
    },
    onError: (error: any) => {
      if (error?.response?.data?.code === 'USER_ALREADY_HAS_ACCOUNT') {
        toast.info('Player already has an account');
        queryClient.setQueryData(['athlete-info', clientId], (oldData: AthleteInfo | undefined) => {
          if (oldData) {
            return {
              ...oldData,
              hasAuthAccount: true,
            };
          }
          return oldData;
        });
      } else {
        toast.error('Failed to send signup link');
        console.error('Failed to send session email invite:', error);
      }
    },
  });

  const handleSendEmailLink = () => {
    sendSessionEmailInviteMutation.mutate();
  };

  const colors = useColors();
  const playerCardColorIndex = Math.max(
    0,
    colors.findIndex((color) => color.lightColor === athleteInfo?.playerCardInfo?.playerCardLightColor)
  );
  const jerseyColorIndex = Math.max(
    0,
    colors.findIndex((color) => color.lightColor === athleteInfo?.jerseyInfo?.jerseyLightColor)
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (
      athleteInfo?.lastSession?.isAssessmentSession ||
      (athleteInfo?.lastSession?.isBaseline && !isV3SessionViewEnabled)
    ) {
      navigate(`/trainer/connects/baseline?trainer=${trainerID}&mboClientId=${clientId}&mboSiteId=${siteId}`, {
        replace: true,
      });
    }
  }, [athleteInfo, navigate, trainerID, clientId, siteId]);

  return (
    <>
      <Helmet>
        <title>MyTOCA | Connects</title>
      </Helmet>

      {isLoading || (isV3SessionViewEnabled && isLoadingPlayerAppointments) ? (
        <LoadingAnimationFullHeight />
      ) : athleteInfo ? (
        isV3SessionViewEnabled ? (
          <div className="flex flex-col items-center justify-center gap-8 py-8 w-full">
            <PlayerCardDisplay
              data={{
                firstName: athleteInfo.firstName,
                lastName: athleteInfo.lastName,
                age: Number(athleteInfo.age) || 0,
                lifeTimeReps: athleteInfo.lifetimeReps || 0,
                jerseyName: athleteInfo.jerseyInfo?.jerseyName || 'TOCA',
                jerseyNumber: athleteInfo.jerseyInfo?.jerseyNumber?.toString() || '',
                jerseyColorIndex: jerseyColorIndex,
                playerCardColorIndex: playerCardColorIndex,
                patternIndex: Number(athleteInfo.playerCardInfo?.patternIndex || 0),
                textColor: athleteInfo.playerCardInfo?.playerCardTextColor || '#000000',
                imgSrc: athleteInfo.playerCardInfo?.imageSrc || DefaultTocaLogo,
                showScore: true,
                averageScore: athleteInfo.averageScore,
              }}
              isTrainerView={true}
              onScoreClick={() => {
                navigate(
                  `/trainer/connects/score?mboClientId=${clientId}&mboSiteId=${siteId}&athleteId=${athleteInfo.profileId}&score=${athleteInfo.averageScore}`
                );
              }}
              containerClassNames="w-[327px] h-[530px]"
            />
            <div className="flex flex-col items-center gap-4">
              {!hasAuthAccount && (
                <Button
                  variant="outline"
                  onClick={handleSendEmailLink}
                  disabled={sendSessionEmailInviteMutation.isLoading}
                >
                  {sendSessionEmailInviteMutation.isLoading ? 'Sending...' : 'Send Signup Link'}
                </Button>
              )}
            </div>
            <div className="mt-4 space-y-6 w-full">
              <NavHeading
                title="My Sessions"
                href={`sessions?mboClientId=${clientId}&mboSiteId=${siteId}&athleteId=${athleteInfo.profileId}`}
              />
              {sessionSummaries.length > 0 ? (
                sessionSummaries.map((session) => <Session key={session._id} session={session} isTrainer={true} />)
              ) : (
                <p className="mt-4 min-h-20 rounded-lg bg-mls-grey p-6 text-center text-base w-full">
                  No completed sessions found
                </p>
              )}
            </div>
            {/* Upcoming Sessions */}
            <div className="mt-6 w-full">
              <UpcomingSessions appointments={futureAppointments} />
            </div>
          </div>
        ) : (
          <div className="grow flex flex-col">
            <LogoBanner />
            <ProfileBar athleteInfo={athleteInfo} />

            {athleteInfo.lastSessionSummary ? (
              <>
                <div className="my-6">
                  <PageHeading text="What We Did" />
                </div>
                <SessionRecap
                  sessionSummary={athleteInfo.lastSessionSummary}
                  lastSession={athleteInfo.lastSession}
                  level={athleteInfo.currentLevel}
                  sessionNumber={athleteInfo.lastSessionNumber} // TODO
                />
              </>
            ) : (
              <div className="grid place-content-center">
                <p className="text-sm text-grey-xdark text-center p-4">Error loading last session summary</p>
              </div>
            )}

            <div className="md:mx-auto md:max-w-[600px] md:w-full mt-8">
              <ButtonTray>
                {athleteInfo?.lastSession?.isBaseline ? (
                  <ConnectsButton
                    text="Previous"
                    color="secondary"
                    onClick={() =>
                      navigate(
                        `/trainer/connects/baseline?trainer=${trainerID}&mboClientId=${clientId}&mboSiteId=${siteId}&baselineVisited=true`
                      )
                    }
                  />
                ) : null}
                <ConnectsButton
                  text="Next"
                  color="secondary"
                  variant="cta"
                  onClick={() =>
                    navigate(
                      `/trainer/connects/schedule?trainer=${trainerID}&mboClientId=${clientId}&mboSiteId=${siteId}`
                    )
                  }
                />
              </ButtonTray>
            </div>
          </div>
        )
      ) : (
        <div className="grow flex flex-col">
          <p className="text-sm text-grey-xdark text-center p-4">unable to load athlete data</p>
        </div>
      )}
    </>
  );
};
